import React, { useEffect, useState } from 'react';
import { RefreshCw, Search } from 'lucide-react';
import { Installation, listInstallations } from '../api/services';
import { Link } from 'react-router-dom';
import './DataViewer.css';

export default function DataViewer() {
  const [installations, setInstallations] = useState<Installation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState({ search: '', status: '', platform: '' });
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    void loadInstallations();
  }, [page, filters.status, filters.platform]);

  const loadInstallations = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await listInstallations(25, page * 25, {
        status: filters.status || undefined,
        platform: filters.platform || undefined,
      });
      setInstallations(response.data);
      setHasMore(response.pagination?.hasMore ?? false);
    } catch (err) {
      setError('Failed to load installations');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const visibleInstallations = installations.filter((installation) => {
    const query = filters.search.trim().toLowerCase();
    return !query || installation.clientId.toLowerCase().includes(query) || installation.clientVersion?.toLowerCase().includes(query);
  });

  if (loading) {
    return <div className="loading">Loading data...</div>;
  }

  return (
    <div className="data-viewer">
      <div className="page-header data-viewer-header">
        <div><h2>Data Viewer</h2><p>Browse all client installations and telemetry data</p></div>
        <button className="btn btn-secondary" onClick={() => void loadInstallations()}><RefreshCw size={16} /> Refresh</button>
      </div>

      {error && <div className="error">{error}</div>}

      <div className="data-filters">
        <label className="data-search"><Search size={16} /><input placeholder="Search client ID or version" value={filters.search} onChange={(event) => setFilters({ ...filters, search: event.target.value })} /></label>
        <select value={filters.status} onChange={(event) => { setFilters({ ...filters, status: event.target.value }); setPage(0); }} aria-label="Filter by status"><option value="">All statuses</option><option value="active">Active</option><option value="disabled">Disabled</option></select>
        <select value={filters.platform} onChange={(event) => { setFilters({ ...filters, platform: event.target.value }); setPage(0); }} aria-label="Filter by platform"><option value="">All platforms</option><option value="darwin">macOS</option><option value="win32">Windows</option><option value="linux">Linux</option></select>
      </div>
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Client ID</th>
              <th>Version</th>
              <th>Platform</th>
              <th>Status</th>
              <th>Last Seen</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {visibleInstallations.length > 0 ? (
              visibleInstallations.map((inst) => (
                <tr key={inst.id}>
                  <td className="monospace">{inst.clientId.substring(0, 8)}...</td>
                  <td>{inst.clientVersion}</td>
                  <td>{inst.platform}</td>
                  <td>
                    <span className={`badge badge-${inst.status === 'active' ? 'success' : 'danger'}`}>
                      {inst.status}
                    </span>
                  </td>
                  <td>{inst.lastSeenAt ? new Date(inst.lastSeenAt).toLocaleString() : 'Never'}</td>
                  <td>
                    <Link to={`/data/${inst.id}`} className="btn-link">
                      View
                    </Link>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} className="empty-cell">
                  No installations found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="data-pagination"><span>Page {page + 1}</span><div><button disabled={page === 0 || loading} onClick={() => setPage(page - 1)}>Previous</button><button disabled={!hasMore || loading} onClick={() => setPage(page + 1)}>Next</button></div></div>
    </div>
  );
}
